import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  BarChart3, Loader2, Plus, Paperclip, Trash2, Users, UserCheck, Clock, X, Lock, Unlock, ChevronDown, ChevronUp, MessageCircle,
} from "lucide-react";

const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const kb = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export const BuildingPolls = ({ building, residents = [] }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDel, setToDel] = useState(null);
  const [expanded, setExpanded] = useState({});

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [audience, setAudience] = useState("all");
  const [selIds, setSelIds] = useState([]);
  const [closesAt, setClosesAt] = useState("");
  const [sendSms, setSendSms] = useState(false);
  const [files, setFiles] = useState([]);

  const active = building.subscription_status === "attivo";

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/buildings/${building.id}/polls`); setPolls(r.data || []); }
    catch { /* silenzioso */ }
    finally { setLoading(false); }
  }, [building.id]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setTitle(""); setBody(""); setOptions(["", ""]); setAudience("all"); setSelIds([]); setClosesAt(""); setFiles([]);
  };
  const openNew = () => {
    if (!active) return toast.error("Condominio non collegato: attivalo per pubblicare sondaggi.");
    resetForm(); setOpen(true);
  };

  const toggleRecipient = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelIds((s) => (s.length === residents.length ? [] : residents.map((r) => r.id)));

  const setOption = (i, v) => setOptions((o) => o.map((x, k) => (k === i ? v : x)));
  const addOption = () => setOptions((o) => [...o, ""]);
  const removeOption = (i) => setOptions((o) => (o.length <= 2 ? o : o.filter((_, k) => k !== i)));

  const addFiles = (e) => { const list = Array.from(e.target.files || []); e.target.value = ""; setFiles((f) => [...f, ...list]); };
  const removeFile = (i) => setFiles((f) => f.filter((_, k) => k !== i));

  const save = async () => {
    if (!title.trim()) return toast.error("Inserisci la domanda del sondaggio.");
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (opts.length < 2) return toast.error("Inserisci almeno 2 opzioni di voto.");
    if (audience === "selected" && selIds.length === 0) return toast.error("Seleziona almeno un destinatario.");
    setSaving(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("body", body.trim());
    fd.append("options", JSON.stringify(opts));
    fd.append("audience", audience);
    fd.append("recipient_ids", JSON.stringify(audience === "selected" ? selIds : []));
    if (closesAt) fd.append("closes_at", new Date(closesAt).toISOString());
    fd.append("send_sms", sendSms ? "true" : "false");
    fd.append("origin_url", window.location.origin);
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/buildings/${building.id}/polls`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sondaggio pubblicato e notificato via email ai destinatari");
      setOpen(false); resetForm();
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Pubblicazione non riuscita"); }
    finally { setSaving(false); }
  };

  const toggleClose = async (p) => {
    try {
      await api.post(`/buildings/${building.id}/polls/${p.id}/close`, { closed: !p.closed });
      toast.success(p.closed ? "Sondaggio riaperto" : "Sondaggio chiuso");
      await load();
    } catch { toast.error("Operazione non riuscita"); }
  };

  const del = async () => {
    if (!toDel) return;
    try {
      await api.delete(`/buildings/${building.id}/polls/${toDel.id}`);
      setPolls((p) => p.filter((x) => x.id !== toDel.id));
      toast.success("Sondaggio eliminato");
      setToDel(null);
    } catch { toast.error("Errore eliminazione"); }
  };

  const downloadAtt = async (poll, att) => {
    try {
      const r = await api.get(`/building-polls/${poll.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Download non riuscito"); }
  };

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden h-full flex flex-col" data-testid="building-polls">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-head text-lg font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#007BFF]" />Sondaggi — {building.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Crea votazioni per i condòmini. Ogni condòmino vota una sola volta dal proprio portale; il voto non è modificabile.</p>
        </div>
        <Button data-testid="poll-new-button" onClick={openNew} disabled={!active} className="bg-[#007BFF] hover:bg-[#0056B3] h-9" title={!active ? "Condominio non collegato" : ""}>
          <Plus className="h-4 w-4 mr-1.5" />Nuovo sondaggio
        </Button>
      </div>

      {loading ? (
        <div className="p-8 flex flex-1 justify-center items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : polls.length === 0 ? (
        <div className="p-8 flex-1 flex items-center justify-center text-center text-muted-foreground text-sm">Nessun sondaggio pubblicato. Usa "Nuovo sondaggio" per chiedere un voto ai condòmini.</div>
      ) : (
        <div className="divide-y divide-border flex-1">
          {polls.map((p) => {
            const total = p.total_votes || 0;
            const isExp = expanded[p.id];
            return (
              <div key={p.id} data-testid={`poll-row-${p.id}`} className="px-6 py-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{p.title}</span>
                      {p.audience === "all"
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-secondary rounded-full px-2 py-0.5 text-muted-foreground"><Users className="h-3 w-3" />Tutti</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-[#007BFF]/10 text-[#007BFF] rounded-full px-2 py-0.5"><UserCheck className="h-3 w-3" />{(p.recipient_names || []).length || (p.recipient_ids || []).length} destinatari</span>}
                      {p.is_open
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5"><Unlock className="h-3 w-3" />Aperto</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5"><Lock className="h-3 w-3" />Chiuso</span>}
                    </div>
                    {p.body && <div className="text-sm text-foreground/80 mt-1 whitespace-pre-wrap">{p.body}</div>}
                    {(p.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {p.attachments.map((a) => (
                          <button key={a.id} data-testid={`poll-att-${a.id}`} onClick={() => downloadAtt(p, a)} className="inline-flex items-center gap-1.5 text-[11px] bg-secondary hover:bg-[#007BFF]/10 hover:text-[#007BFF] border border-border rounded-md px-2 py-1 transition-colors">
                            <Paperclip className="h-3 w-3" />{a.filename}<span className="text-muted-foreground">· {kb(a.size)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Risultati */}
                    <div className="mt-3 space-y-2" data-testid={`poll-results-${p.id}`}>
                      {(p.options || []).map((o) => {
                        const pct = total > 0 ? Math.round((o.count / total) * 100) : 0;
                        return (
                          <div key={o.id}>
                            <div className="flex items-center justify-between text-xs mb-0.5"><span>{o.text}</span><span className="text-muted-foreground">{o.count} · {pct}%</span></div>
                            <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[#007BFF] rounded-full" style={{ width: `${pct}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-2 flex-wrap">
                      <span>{fmtDateTime(p.created_at)}</span>
                      <span>·</span><span>{total} vot{total === 1 ? "o" : "i"}</span>
                      {p.closes_at && <><span>·</span><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Chiude {fmtDateTime(p.closes_at)}</span></>}
                      {total > 0 && (
                        <button data-testid={`poll-voters-toggle-${p.id}`} onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))} className="inline-flex items-center gap-1 text-[#007BFF] hover:underline">
                          {isExp ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}Chi ha votato
                        </button>
                      )}
                    </div>
                    {isExp && total > 0 && (
                      <div className="mt-2 border border-border rounded-md divide-y divide-border" data-testid={`poll-voters-${p.id}`}>
                        {(p.voters || []).map((v, i) => {
                          const opt = (p.options || []).find((o) => o.id === v.option_id);
                          return (
                            <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                              <span className="font-medium">{v.name}</span>
                              <span className="text-[#007BFF]">{opt?.text || "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button data-testid={`poll-close-${p.id}`} onClick={() => toggleClose(p)} title={p.closed ? "Riapri" : "Chiudi"} className="p-1.5 rounded-md hover:bg-secondary hover:text-[#007BFF] text-muted-foreground">{p.closed ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</button>
                    <button data-testid={`poll-delete-${p.id}`} onClick={() => setToDel(p)} title="Elimina" className="p-1.5 rounded-md hover:bg-secondary hover:text-red-600 text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && !saving && setOpen(false)}>
        <DialogContent data-testid="poll-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#007BFF]" />Nuovo sondaggio</DialogTitle>
            <DialogDescription>{building.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Domanda</label>
              <Input data-testid="poll-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Approvate il preventivo per il rifacimento facciata?" className="h-9 mt-1" autoFocus />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Testo / descrizione — facoltativo</label>
              <Textarea data-testid="poll-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Dettagli del sondaggio…" className="mt-1 text-sm resize-none" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Opzioni di voto (min. 2)</label>
              <div className="space-y-1.5 mt-1">
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`poll-option-${i}`} value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Opzione ${i + 1}`} className="h-9" />
                    {options.length > 2 && <button type="button" onClick={() => removeOption(i)} className="text-muted-foreground hover:text-red-600 shrink-0"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
              <button data-testid="poll-add-option" type="button" onClick={addOption} className="mt-1.5 text-xs text-[#007BFF] hover:underline inline-flex items-center gap-1"><Plus className="h-3 w-3" />Aggiungi opzione</button>
            </div>
            <label data-testid="poll-sms-toggle" className="flex items-center gap-2.5 cursor-pointer rounded-md border border-border px-3 py-2.5 hover:bg-secondary/50">
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="accent-emerald-600 h-4 w-4" data-testid="poll-sms-checkbox" />
              <MessageCircle className={`h-4 w-4 ${sendSms ? "text-emerald-600" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">Avvisa via SMS</div>
                <div className="text-[11px] text-muted-foreground">Invia un SMS ai condòmini con il link al portale per votare (usa la quota SMS del condominio · piani Premium/Elite).</div>
              </div>
            </label>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Destinatari</label>
              <div className="flex gap-2 mt-1">
                <button data-testid="poll-audience-all" type="button" onClick={() => setAudience("all")} className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-sm font-medium border transition-colors ${audience === "all" ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border hover:bg-secondary"}`}><Users className="h-4 w-4" />Tutti</button>
                <button data-testid="poll-audience-selected" type="button" onClick={() => setAudience("selected")} className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-sm font-medium border transition-colors ${audience === "selected" ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border hover:bg-secondary"}`}><UserCheck className="h-4 w-4" />Solo scelti</button>
              </div>
              {audience === "selected" && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-muted-foreground">{selIds.length}/{residents.length} selezionati</span>
                    {residents.length > 0 && <button type="button" data-testid="poll-toggle-all" onClick={toggleAll} className="text-[11px] text-[#007BFF] hover:underline">{selIds.length === residents.length ? "Deseleziona tutti" : "Seleziona tutti"}</button>}
                  </div>
                  {residents.length === 0 ? (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Nessun condòmino in anagrafica.</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="poll-recipients">
                      {residents.map((r) => (
                        <label key={r.id} data-testid={`poll-recipient-${r.id}`} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-secondary/50 text-sm">
                          <input type="checkbox" checked={selIds.includes(r.id)} onChange={() => toggleRecipient(r.id)} className="accent-[#007BFF]" />
                          <span className="font-medium">{r.nome}</span>
                          {r.email && <span className="text-xs text-muted-foreground ml-auto truncate">{r.email}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Chiusura (data e ora) — facoltativa</label>
              <Input data-testid="poll-closes" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="h-9 mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">Dopo questa data non sarà più possibile votare. Puoi anche chiudere il sondaggio manualmente in qualsiasi momento.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Allegati — facoltativi</label>
              <label className="mt-1 flex items-center gap-2 cursor-pointer text-sm text-[#007BFF] hover:underline w-fit">
                <input data-testid="poll-files" type="file" multiple className="hidden" onChange={addFiles} />
                <Paperclip className="h-4 w-4" />Aggiungi file
              </label>
              {files.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="poll-files-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-secondary rounded-md px-2.5 py-1.5">
                      <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{f.name}</span>
                      <span className="text-muted-foreground">{kb(f.size)}</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annulla</Button>
            <Button data-testid="poll-publish" onClick={save} disabled={saving} className="bg-[#007BFF] hover:bg-[#0056B3]">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Pubblicazione…</> : <><BarChart3 className="h-4 w-4 mr-2" />Pubblica sondaggio</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDel} onOpenChange={(o) => !o && setToDel(null)}>
        <AlertDialogContent data-testid="delete-poll-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il sondaggio?</AlertDialogTitle>
            <AlertDialogDescription>Il sondaggio <b>{toDel?.title}</b> e tutti i voti raccolti saranno eliminati definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-poll" onClick={(e) => { e.preventDefault(); del(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
